export const LOCALES = ["en", "es", "de", "fr"] as const;
export type Locale = (typeof LOCALES)[number];

export const TARGET_LOCALES = ["es", "de", "fr"] as const;

export const LOCALE_FLAGS: Record<string, string> = { es: "🇪🇸", de: "🇩🇪", fr: "🇫🇷" };

export function emptyLocale(): Record<string, string> {
  return { en: "", es: "", de: "", fr: "" };
}

/** Read a localized value — handles string, object {en,es,...}, or number */
export function getLocalizedText(val: any, locale: string = "en"): string {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number") return String(val);
  if (typeof val === "object" && !Array.isArray(val)) return val[locale] || val.en || "";
  return "";
}

/** Shorthand: extract English text from a locale object or string */
export function deloc(v: any): string {
  return getLocalizedText(v, "en");
}

/** Update a single locale on a localized field, preserving other locales */
export function updateLocalizedText(block: any, key: string, locale: string, newText: string): any {
  const val = block[key];
  if (typeof val === "object" && val !== null && !Array.isArray(val)) {
    return { ...block, [key]: { ...val, [locale]: newText } };
  }
  return { ...block, [key]: newText };
}

/** Set locale on a value directly, creating locale object if needed */
export function setLocaleValue(val: any, locale: string, newText: string): Record<string, string> {
  const obj = typeof val === "object" && val !== null ? { ...val } : { en: val || "" };
  obj[locale] = newText;
  return obj;
}

/** Normalize a string-or-record to locale object */
export function toLocaleObject(title: string | Record<string, string>): Record<string, string> {
  if (typeof title === "string") return { en: title };
  return title || { en: "" };
}

/** Returns true if all target locales are filled */
export function isFullyTranslated(val: any): boolean {
  if (typeof val !== "object" || Array.isArray(val) || !val) return false;
  return TARGET_LOCALES.every((l) => typeof val[l] === "string" && val[l].trim().length > 0);
}

/** Check if any block in content tree has locale content */
export function hasLocaleContent(content: any[], locale: string): boolean {
  if (!content || !Array.isArray(content)) return false;
  return content.some((block: any) => {
    if (!block) return false;
    if (block.text && typeof block.text === "object" && block.text[locale]?.trim()) return true;
    if (block.title && typeof block.title === "object" && block.title[locale]?.trim()) return true;
    if (block.type === "group" && Array.isArray(block.content)) {
      return hasLocaleContent(block.content, locale);
    }
    return false;
  });
}

/** Count translatable fields and how many have content for a locale */
export function countLocaleFields(blocks: any[], locale: string): { total: number; filled: number } {
  let total = 0;
  let filled = 0;
  if (!blocks || !Array.isArray(blocks)) return { total, filled };
  for (const block of blocks) {
    if (!block) continue;
    if (block.text && typeof block.text === "object" && "en" in block.text) {
      total++;
      if (block.text[locale]?.trim()) filled++;
    }
    if (block.title && typeof block.title === "object" && "en" in block.title) {
      total++;
      if (block.title[locale]?.trim()) filled++;
    }
    if (block.type === "group" && Array.isArray(block.content)) {
      const sub = countLocaleFields(block.content, locale);
      total += sub.total;
      filled += sub.filled;
    }
  }
  return { total, filled };
}

/** Get locale translation status: full / partial / empty */
export function getLocaleStatus(content: any[], locale: string): "full" | "partial" | "empty" {
  const { total, filled } = countLocaleFields(content, locale);
  if (total === 0 || filled === 0) return "empty";
  if (filled >= total) return "full";
  return "partial";
}
